import _ from 'lodash'

import {
  ConfigurableProductDetailsFragment,
  ConfigurableVariantIndex,
  GetOrderProductsQuery,
  SimpleProductDetailsFragment,
} from '../graphql/magento'
import { CartItem, CartShippingAddress } from '../lib/cart/types'
import { getSkuFromUrlHash } from '../lib/products/utils/pdp'
import { shouldShowCaliforniaProp65Asterisks } from './cartTools'
import { groupBy } from './dataTools'
import { Maybe } from './types'
import { UserCategory } from './userClaims'

import dataSheetIcon from '../img/data_sheets/dataSheet.svg'

export type ContentBlock = (typeof contentBlocks)[number]

export type AttributeName = string
export type AttributeValue = string
export type ProductSku = string
export type AttributeSkuMapping = {
  [key in AttributeValue]: AttributeSkuMapping | ProductSku
}

export interface FormattedVariantData {
  // ["attr1", "attr2"]
  usedAttributes: string[]
  // { "attr1": ["attr1-value1", "attr1-value2"], "attr2": ["attr2-value1", ...] }
  attributeValues: Record<AttributeName, AttributeValue[]>
  // { "attr1": { "attr1-value1": ["SKU1"], "attr1-value2": ["SKU2"] } }
  attributeProductList: Record<AttributeName, Record<AttributeValue, ProductSku[]>>
  // ["SKU1", "SKU2"]
  availableVariantSkus: ProductSku[]
  // { "SKU1": { attr1: "attr1-value1", attr2: "attr2-value1", "SKU2:: { ... } }}
  productIndex: Record<ProductSku, Record<AttributeName, AttributeValue>>
  // nested as deep as `usedAttributes.length`
  // { "attr1-value1": { "attr2-value1": "SKU1", "attr2-value2": "SKU2", "attr1-value2": { ... }}
  mapping: AttributeSkuMapping
}

export const totalItemsDisplay = (quantity: number): string =>
  `${quantity} Item${quantity === 1 ? '' : 's'}`

export const contentBlocks = [
  'content_right_public',
  'content_right_patient',
  'content_right_professional',
  'content_right_both',
  'content_left_public',
  'content_left_patient',
  'content_left_professional',
  'content_left_both',
] as const

export const formattedVariantData = (item: {
  variant_index: ConfigurableVariantIndex
}): FormattedVariantData => {
  const { variant_index: variantData } = item
  const productIndex = JSON.parse(variantData.product_index || '{}') as Record<
    ProductSku,
    Record<AttributeName, AttributeValue>
  >
  const availableVariantSkus = Object.keys(productIndex)

  return {
    usedAttributes: (variantData.used_attributes || []) as AttributeName[],
    attributeValues: JSON.parse(variantData.attribute_values || '{}'),
    attributeProductList: JSON.parse(variantData.attribute_product_list || '{}'),
    availableVariantSkus,
    productIndex,
    mapping: JSON.parse(variantData.mapping || '{}'),
  }
}

const allowedVariantValuesByAttribute = ({
  allowedCombinations,
  usedAttributes,
}: {
  usedAttributes: AttributeName[]
  allowedCombinations: Record<AttributeName, AttributeValue>[]
}): Record<AttributeName, AttributeValue[]> | null => {
  if (allowedCombinations.length) {
    return usedAttributes.reduce<Record<string, string[]>>(
      (collection, attribute) => ({
        ...collection,
        [attribute]: Object.keys(groupBy({ arrayHash: allowedCombinations, groupOn: attribute })),
      }),
      {} as Record<AttributeName, AttributeValue[]>,
    )
  }

  return null
}

// Allowed variant combinations based on user selections
export const allowedVariantSelections = ({
  usedAttributes,
  selections,
  attributeProductList,
  productIndex,
  availableVariantSkus,
}: FormattedVariantData & { selections: Record<AttributeName, AttributeValue> }): Record<
  AttributeName,
  AttributeValue[]
> | null => {
  // Loop through each used attribute and check if the customer has selected an option for it
  let allowedProducts: ProductSku[] = []
  const selectedOptions: AttributeValue[] = []
  usedAttributes.forEach((attributeCode) => {
    /**
     * If this attribute has a selected option, use the "attribute_product_list",
     * to get a list of products the selected option is valid for, and add it to the allowedProducts
     * array. This will allow us to consider use cases where there are more than
     * 2 configurable attributes
     */
    if (selections[attributeCode]) {
      const selectedOption: AttributeValue = selections[attributeCode]
      selectedOptions.push(selectedOption)
      const selectedAllowedProducts: ProductSku[] =
        attributeProductList[attributeCode][selectedOption]

      // Add the products to the allowedProducts array and remove duplicates
      allowedProducts = allowedProducts.concat(
        selectedAllowedProducts.filter((product) => allowedProducts.indexOf(product) < 0),
      )
    }
  })

  allowedProducts = allowedProducts.length ? allowedProducts : availableVariantSkus

  const allowedCombinations: Record<AttributeName, AttributeValue>[] = allowedProducts
    .map((product) => productIndex[product])
    .filter((product) => product)

  return allowedVariantValuesByAttribute({ allowedCombinations, usedAttributes })
}

export const availableVariantSelections = ({
  usedAttributes,
  productIndex,
  availableVariantSkus,
}: FormattedVariantData): Record<AttributeName, AttributeValue[]> | null => {
  const allowedCombinations: Record<AttributeName, AttributeValue>[] = availableVariantSkus.map(
    (p) => productIndex[p],
  )

  return allowedVariantValuesByAttribute({ allowedCombinations, usedAttributes })
}

export const defaultItemFormValues = ({
  item,
  availableVariantSkus,
  usedAttributes,
  availableVariantOptions,
  attributeValues,
  productIndex,
}: FormattedVariantData & {
  item: ConfigurableProductDetailsFragment
  availableVariantOptions: Record<AttributeName, AttributeValue[]> | null
}): Record<AttributeName, AttributeValue> => {
  // If there is a valid hash in the URL, use that to select the variant
  const hash = getSkuFromUrlHash()
  if (hash && availableVariantSkus.includes(hash)) {
    const selectedVariant = productIndex[hash]
    return usedAttributes.reduce(
      (collection, attribute) => ({
        ...collection,
        [attribute]: selectedVariant[attribute],
      }),
      {},
    )
  }
  // If there is only one variant, select it by default
  if (availableVariantSkus.length === 1) {
    return usedAttributes.reduce(
      (collection, attribute) => ({
        ...collection,
        [attribute]: availableVariantOptions?.[attribute][0],
      }),
      {},
    )
  }
  // Find the first in-stock variant
  const inStockVariant = item?.variants?.find(
    (variant) => variant?.product?.stock_status === 'IN_STOCK',
  )
  if (inStockVariant?.product?.sku) {
    const selectedVariant = productIndex[inStockVariant.product.sku]
    return usedAttributes.reduce(
      (collection, attribute) => ({
        ...collection,
        [attribute]: selectedVariant[attribute],
      }),
      {},
    )
  }
  // Otherwise select the first option of each attribute as default
  return usedAttributes.reduce(
    (collection, attribute) => ({
      ...collection,
      [attribute]: attributeValues[attribute][0],
    }),
    {},
  )
}

export const displayAttribute = <
  TAttr extends string,
  TValue,
  TItem extends Partial<Record<TAttr, TValue>>,
  TVariant extends Partial<Record<TAttr, TValue>>,
>({
  item,
  selectedVariant,
  attribute,
}: {
  item?: TItem | null
  selectedVariant?: TVariant | null
  attribute: TAttr
}): TItem[TAttr] | TVariant[TAttr] | null | undefined =>
  selectedVariant?.[attribute] || item?.[attribute]

export const isCombinationNotAvailable = ({
  hasSelectedVariantCombination,
  variantSelections,
  productIndex,
}: {
  hasSelectedVariantCombination: boolean
  variantSelections: Record<AttributeName, AttributeValue>
  productIndex: FormattedVariantData['productIndex']
}): boolean =>
  hasSelectedVariantCombination &&
  !Object.values(productIndex).find((option) => _.isEqual(variantSelections, option))

export const stockMessageDisplay = ({
  selectedVariant,
  combinationNotAvailable,
}: {
  selectedVariant?: SimpleProductDetailsFragment | null
  combinationNotAvailable: boolean
}): string => {
  if (selectedVariant && selectedVariant?.stock_status !== 'IN_STOCK') {
    return selectedVariant.out_of_stock_message || 'Out of stock'
  }

  if (combinationNotAvailable) {
    return "The combination you've selected is not available.  Please choose an alternate."
  }

  return ''
}

export const dataSheets = ({
  item,
  selectedVariant,
}: {
  item: ConfigurableProductDetailsFragment
  selectedVariant: SimpleProductDetailsFragment | null
}) => ({
  doc_patient_education: {
    id: 'doc_patient_education',
    href: displayAttribute({
      item,
      selectedVariant,
      attribute: 'doc_patient_education',
    }),
    text: 'Read more Patient Education',
  },
  doc_tech_sheet: {
    id: 'doc_tech_sheet',
    href: displayAttribute({
      item,
      selectedVariant,
      attribute: 'doc_tech_sheet',
    }),
    text: 'Learn more from Tech Sheet',
  },
  doc_clinical_trials: {
    id: 'doc_clinical_trials',
    href: displayAttribute({
      item,
      selectedVariant,
      attribute: 'doc_clinical_trials',
    }),
    text: 'Read our clinical research',
  },
})

export const dataSheetImage = (): string => {
  return dataSheetIcon
}

export const itemDisplayNameVariantInfo = ({
  itemAttributes,
}: {
  itemAttributes: CartItem['item_attributes']
}): string => {
  if (itemAttributes?.flavor_size) {
    return `, ${itemAttributes.flavor_size}`
  }

  if (itemAttributes?.flavor) {
    return `, ${itemAttributes.flavor}`
  }

  if (itemAttributes?.dosage) {
    return `, ${itemAttributes.dosage}`
  }

  return ''
}

export const itemFlavorText = ({
  itemAttributes,
}: {
  itemAttributes: CartItem['item_attributes']
}): string => itemAttributes?.flavor ?? itemAttributes?.flavor_size ?? ''

export const cartItemDisplayName = ({
  item,
  shippingAddress = null,
}: {
  item: CartItem
  shippingAddress?: CartShippingAddress | null
}): string =>
  `${shouldShowCaliforniaProp65Asterisks({ item, shippingAddress }) ? '** ' : ''}${
    item.product.name
  }${itemDisplayNameVariantInfo({ itemAttributes: item.item_attributes })}${
    item.item_attributes?.size ? ` - ${item.item_attributes.size}` : ''
  }`.toUpperCase()

type OrderProductsItem = NonNullable<
  NonNullable<GetOrderProductsQuery['products']>['items']
>[number]
type OrderProductsItemSimpleProduct = Extract<
  NonNullable<OrderProductsItem>,
  { __typename?: 'SimpleProduct' }
>

export const autoShipItemDisplayName = (item: OrderProductsItemSimpleProduct): string => {
  const attributeValues: Record<AttributeName, AttributeValue> = item?.variant_index
    ?.attribute_values
    ? JSON.parse(item.variant_index.attribute_values)
    : {}

  return `${item.name}${itemDisplayNameVariantInfo({ itemAttributes: attributeValues })}${
    attributeValues.size ? `, ${attributeValues.size}` : ''
  }`
}

export const autoShipItemName = (item: OrderProductsItemSimpleProduct): string => {
  const attributeValues: Record<AttributeName, AttributeValue> = item?.variant_index
    ?.attribute_values
    ? JSON.parse(item.variant_index.attribute_values)
    : {}

  return `${item.name}${itemDisplayNameVariantInfo({ itemAttributes: attributeValues })}`
}

export const insufficientStockError = (quantity: string | number): string => `
  Requested quantity (${quantity}) is not available in stock.
  Your current quantity is still available for purchase.
`

// For now just assume that if the error contains qty then the issue is that there is not
// enough of the product in stock for the user to purchase the amount they requested. Not
// comparing the entire error string to avoid issues where a slight change in the error
// message breaks the frontend display of errors.
export const shouldShowInsufficientStockError = ({ error }: { error: string }): boolean =>
  error.includes('qty')

export const stripHtmlTags = ({ text }: { text: string }): string => text.replace(/<(.|\n)*?>/g, '')

export const truncatedNonHtmlText = ({
  text,
  wordCount,
}: {
  text: string
  wordCount: number | null | undefined
}): string =>
  stripHtmlTags({ text })
    .split(' ')
    .slice(0, wordCount || 5)
    .join(' ')

/**
 * Honors whichever quantity is greater.
 * Defaults to 1 if necessary.
 */
export const reorderItemQuantity = ({
  minSaleQty,
  prevOrderQty = 0,
}: {
  minSaleQty: Maybe<string>
  prevOrderQty?: number
}): number => {
  const minSaleQuantity = Number(minSaleQty ?? 0)
  return Math.max(minSaleQuantity, prevOrderQty, 1)
}

export const maxItemOrderQuantityByUserCategory = (
  userCategory: UserCategory | undefined,
): number | undefined => {
  switch (userCategory) {
    case 'practitioner':
    case 'other_professional':
      return undefined
    case 'employee':
    case 'patient':
    default:
      return 10
  }
}
